import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Download,
  FileText,
  PieChart,
  BarChart3,
  Filter,
  ChevronDown,
  ChevronUp,
  Clock,
  Wallet,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../utils/helpers';

// Import extracted report components
import PlayerBalancesReport from './reports/PlayerBalancesReport';
import GameExpensesReport from './reports/GameExpensesReport';
import SummaryReport from './reports/SummaryReport';
import ExpenseBreakdownReport from './reports/ExpenseBreakdownReport';
import TransactionAuditReport from './reports/TransactionAuditReport';
import TournamentPlayerSummary from './reports/TournamentPlayerSummary';
import RegistrationsReport from './reports/InitialDonationsReport';
import PlayersReport from './reports/PlayersReport';

const FinancialReports = ({
  expenses,
  walletTransactions,
  players,
  games,
  teams,  // NEW: teams array
  selectedClub,
  clubName,
  tournaments,  // NEW: tournaments array
  user,  // NEW: user prop for treasurer role checking
  canApprove,  // NEW: passed from parent if they already checked
  userRole,  // NEW: passed from parent for display
  paymentSubmissions = []
}) => {
  const [reportType, setReportType] = useState('summary');
  const [dateRange, setDateRange] = useState('all-time');
  const [selectedTournament, setSelectedTournament] = useState('all'); // NEW: tournament filter
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedPlayers, setExpandedPlayers] = useState({}); // NEW: for player breakdown

  // ============ TREASURER ROLE CHECK ============
  // Use passed prop or calculate locally
  const isTreasurer = () => {
    if (canApprove !== undefined) return canApprove;

    const userRoleValue = user?.clubInfo?.role;
    const additionalRoles = user?.clubInfo?.additionalRoles || [];
    const allRoles = user?.clubInfo?.roles || [];

    return userRoleValue === 'treasurer' ||
      userRoleValue === 'super-admin' ||
      additionalRoles.includes('treasurer') ||
      allRoles.includes('treasurer');
  };

  const canApproveExpenses = isTreasurer();

  // Get treasurer information for display
  const getTreasurerInfo = () => {
    const treasurers = players.filter(p =>
      p.clubInfo?.role === 'treasurer' ||
      p.clubInfo?.additionalRoles?.includes('treasurer')
    );

    if (treasurers.length > 0) {
      return {
        name: treasurers[0].name,
        email: treasurers[0].email,
        count: treasurers.length
      };
    }
    return null;
  };

  const treasurerInfo = getTreasurerInfo();
  // ============ END TREASURER ROLE CHECK ============

  // Filter data by date range and tournament
  const getFilteredData = () => {
    const now = new Date();
    let startDate = new Date(0); // Beginning of time

    switch (dateRange) {
      case 'this-month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last-month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        break;
      case 'this-year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'last-year':
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        break;
      default:
        break;
    }

    let filteredExpenses = expenses.filter(exp =>
      new Date(exp.createdAt) >= startDate &&
      exp.approvalStatus === 'approved'
    );

    // Filter out orphaned expenses (where game was deleted)
    // Only filter if games array is available
    if (games && Array.isArray(games)) {
      const gameIds = new Set(games.map(g => g.id));
      filteredExpenses = filteredExpenses.filter(exp => {
        // If expense has a gameId, verify the game still exists
        if (exp.gameId && exp.gameId !== '') {
          const gameExists = gameIds.has(exp.gameId);
          if (!gameExists) {
            console.log('⚠️ Filtering out orphaned expense (game deleted):', {
              expenseId: exp.id,
              description: exp.description,
              amount: exp.amount,
              gameId: exp.gameId
            });
            return false;
          }
        }
        return true;
      });
    }

    let filteredWallet = walletTransactions.filter(txn =>
      new Date(txn.createdAt) >= startDate
    );

    // Filter out orphaned wallet transactions (where game was deleted)
    // Only filter if games array is available
    if (games && Array.isArray(games)) {
      const gameIds = new Set(games.map(g => g.id));
      filteredWallet = filteredWallet.filter(txn => {
        // If transaction has a gameId, verify the game still exists
        if (txn.gameId && txn.gameId !== '') {
          const gameExists = gameIds.has(txn.gameId);
          if (!gameExists) {
            console.log('⚠️ Filtering out orphaned wallet transaction (game deleted):', {
              txnId: txn.id,
              description: txn.description,
              amount: txn.amount,
              gameId: txn.gameId
            });
            return false;
          }
        }
        return true;
      });
    }

    // Filter by tournament if selected
    if (selectedTournament && selectedTournament !== 'all') {
      const tournamentGames = games.filter(g => g.tournamentId === selectedTournament);
      const tournamentGameIds = tournamentGames.map(g => g.id);

      filteredExpenses = filteredExpenses.filter(exp =>
        // Include tournament fee expenses for this tournament
        (exp.isTournamentFee && exp.tournamentId === selectedTournament) ||
        // Include game expenses for games in this tournament
        (exp.gameId && tournamentGameIds.includes(exp.gameId))
      );

      // Filter wallet transactions by tournament
      filteredWallet = filteredWallet.filter(txn =>
        txn.tournamentId === selectedTournament ||
        (txn.gameId && tournamentGameIds.includes(txn.gameId))
      );
    }

    return { filteredExpenses, filteredWallet };
  };

  const { filteredExpenses, filteredWallet } = getFilteredData();

  // Calculate summary statistics with PROPER FINANCIAL TERMINOLOGY
  const calculateSummary = () => {
    // IMPORTANT DISTINCTIONS:
    // - Receivables = Money OWED to the club (Initial Donations assigned to players)
    // - Collected = Money ACTUALLY RECEIVED (wallet transactions showing payments)
    // - Outstanding = Receivables - Collected (money still owed)
    // - Expenses = Money SPENT by the club (non-registration, actual costs)

    // Separate Initial Donations (RECEIVABLES) from actual expenses (COSTS)
    const registrationFees = filteredExpenses.filter(exp => exp.isRegistrationFee || exp.isInitialDonation);
    const actualExpenses = filteredExpenses.filter(exp => !exp.isRegistrationFee && !exp.isInitialDonation);

    // RECEIVABLES = Total Initial Donations assigned to players (what they OWE)
    const totalExpectedReceivables = registrationFees.reduce((sum, exp) => sum + (exp.amount || 0), 0);

    // COLLECTED = Money actually received from players
    // Wallet transactions with type 'payment' and negative amounts represent payments received
    // (negative because it reduces what the player owes)
    const paymentsReceived = (walletTransactions || []).filter(txn =>
      txn.type === 'payment' && txn.amount < 0
    );
    const totalCollected = Math.abs(paymentsReceived.reduce((sum, txn) => sum + (txn.amount || 0), 0));

    // RECEIVABLES = What players still owe (Expected - Collected)
    const totalReceivables = Math.max(0, totalExpectedReceivables - totalCollected);

    // EXPENSES = Actual costs (non-Initial Donations)
    const totalExpenses = actualExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

    // REIMBURSEMENTS = Money paid back to players who covered expenses
    const reimbursements = (walletTransactions || []).filter(txn =>
      txn.type === 'reimbursement' || (txn.type !== 'payment' && txn.amount > 0)
    );
    const totalReimbursements = reimbursements.reduce((sum, txn) => sum + (txn.amount || 0), 0);

    // PENDING = Expenses awaiting approval (excluding Initial Donations)
    const pendingExpenses = expenses.filter(exp => exp.approvalStatus === 'pending' && !exp.isRegistrationFee && !exp.isInitialDonation);
    const totalPending = pendingExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

    // CASH POSITION = Collected - Expenses - Reimbursements (actual money the club has)
    const cashPosition = totalCollected - totalExpenses - totalReimbursements;

    // Group ACTUAL EXPENSES by category (not Initial Donations)
    const byCategory = {};
    actualExpenses.forEach(exp => {
      const category = exp.type || 'Other';
      byCategory[category] = (byCategory[category] || 0) + exp.amount;
    });

    // Group ACTUAL EXPENSES by game (not Initial Donations)
    const byGame = {};
    actualExpenses.forEach(exp => {
      if (exp.gameId) {
        const game = games.find(g => g.id === exp.gameId);
        const gameName = game ? game.name : 'Unknown Game';
        byGame[gameName] = (byGame[gameName] || 0) + exp.amount;
      } else {
        byGame['Club Expenses'] = (byGame['Club Expenses'] || 0) + exp.amount;
      }
    });

    // AUDIT RECONCILIATION CHECKS
    const playerBalances = calculatePlayerBalances();

    // Check 1: Sum of paidUpfront should equal totalExpenses
    const totalPaidUpfront = playerBalances.reduce((sum, p) => sum + p.paidUpfront, 0);
    const paidUpfrontDiff = Math.abs(totalPaidUpfront - totalExpenses);
    if (paidUpfrontDiff > 0.01) { // Allow 1 cent rounding difference
      console.warn('⚠️ AUDIT WARNING: Sum of player paidUpfront does not match totalExpenses', {
        totalPaidUpfront: totalPaidUpfront.toFixed(2),
        totalExpenses: totalExpenses.toFixed(2),
        difference: paidUpfrontDiff.toFixed(2)
      });
    }

    // Check 2: Sum of reimbursed should equal totalReimbursements
    const totalPlayerReimbursed = playerBalances.reduce((sum, p) => sum + p.reimbursed, 0);
    const reimbursedDiff = Math.abs(totalPlayerReimbursed - totalReimbursements);
    if (reimbursedDiff > 0.01) {
      console.warn('⚠️ AUDIT WARNING: Sum of player reimbursed does not match totalReimbursements', {
        totalPlayerReimbursed: totalPlayerReimbursed.toFixed(2),
        totalReimbursements: totalReimbursements.toFixed(2),
        difference: reimbursedDiff.toFixed(2)
      });
    }

    // Financial reconciliation logging
    console.log('📊 Financial Reconciliation:', {
      expectedReceivables: totalExpectedReceivables.toFixed(2),
      collected: totalCollected.toFixed(2),
      receivables: totalReceivables.toFixed(2),
      expenses: totalExpenses.toFixed(2),
      reimbursements: totalReimbursements.toFixed(2),
      cashPosition: cashPosition.toFixed(2)
    });

    return {
      // NEW: Proper financial metrics
      totalExpectedReceivables: Math.round(totalExpectedReceivables * 100) / 100,
      totalCollected: Math.round(totalCollected * 100) / 100,
      totalReceivables: Math.round(totalReceivables * 100) / 100,
      totalExpenses: Math.round(totalExpenses * 100) / 100,
      totalReimbursements: Math.round(totalReimbursements * 100) / 100,
      cashPosition: Math.round(cashPosition * 100) / 100,
      totalPending: Math.round(totalPending * 100) / 100,
      pendingCount: pendingExpenses.length,
      registrationFeeCount: registrationFees.length,
      paymentsReceivedCount: paymentsReceived.length,
      byCategory,
      byGame,
      avgExpensePerGame: Object.keys(byGame).filter(k => k !== 'Club Expenses').length > 0
        ? Math.round((totalExpenses / Object.keys(byGame).filter(k => k !== 'Club Expenses').length) * 100) / 100
        : 0
    };
  };

  // Calculate player balances
  const calculatePlayerBalances = () => {
    const balances = {};

    // Validation: Track warnings for audit purposes
    const warnings = [];

    // Helper to check if a player ID is external
    const isExternalPlayer = (playerId) => {
      if (!playerId) return false;
      return playerId.startsWith('guest-') ||
        playerId.startsWith('guest_') ||
        playerId.startsWith('player_');
    };

    // Initialize with all registered players
    players.forEach(player => {
      if (!player.id || !player.name) {
        warnings.push(`Invalid player data: ${JSON.stringify(player)}`);
        return;
      }
      balances[player.id] = {
        id: player.id,
        name: player.name,
        paidUpfront: 0,           // Expenses player submitted (club owes them)
        reimbursed: 0,            // What they've been reimbursed
        paymentsMade: 0,          // Payments made to club
        waivers: 0,               // Waivers received
        owesForExpenses: 0,       // Their share of game/tournament expenses + merch (NOT registration)
        tournamentFees: 0,        // Tournament fee portion
        gameExpenses: 0,          // Game expenses portion  
        merchandiseFees: 0,       // Merchandise portion
        registrationFee: 0,       // Initial Donation amount
        registrationPaid: false,  // Whether registration is paid
        registrationWaived: false, // Whether registration was waived
        registrationPaidAmount: 0,
        balance: 0,
        isExternal: player.isExternal || player.isManual || player.isGuest || isExternalPlayer(player.id),
        uniquePlayerIds: [
          player.id,
          player.userId,
          player.oderId,
          player.oderedPlayerId,
          ...(player.matchedPlayerIds || [])
        ].filter(Boolean),
        playerEmails: [
          player.email,
          ...(player.matchedPlayerEmails || [])
        ].filter(Boolean).map(e => e.toLowerCase())
      };
    });

    // Also add external/guest players from games and tournament fee splitAmongDetails
    // Check games for guest players
    games.forEach(game => {
      if (game.guestPlayers) {
        game.guestPlayers.forEach(guest => {
          if (guest.id && !balances[guest.id]) {
            balances[guest.id] = {
              id: guest.id,
              name: guest.name || 'Guest Player',
              paidUpfront: 0,
              reimbursed: 0,
              paymentsMade: 0,
              waivers: 0,
              owesForExpenses: 0,
              tournamentFees: 0,
              gameExpenses: 0,
              merchandiseFees: 0,
              registrationFee: 0,
              registrationPaid: false,
              registrationWaived: false,
              registrationPaidAmount: 0,
              balance: 0,
              isExternal: true,
              uniquePlayerIds: [guest.id],
              playerEmails: []
            };
          }
        });
      }
    });

    // Check tournament fees for external players in splitAmongDetails
    filteredExpenses.forEach(exp => {
      if (exp.isTournamentFee && exp.settled && exp.splitAmongDetails) {
        exp.splitAmongDetails.forEach(playerShare => {
          const playerId = playerShare.oderId;
          if (playerId && !balances[playerId]) {
            balances[playerId] = {
              id: playerId,
              name: playerShare.name || 'Unknown Player',
              paidUpfront: 0,
              reimbursed: 0,
              paymentsMade: 0,
              owesForExpenses: 0,
              tournamentFees: 0,
              gameExpenses: 0,
              registrationFee: 0,
              registrationPaid: false,
              registrationPaidAmount: 0,
              balance: 0,
              isExternal: playerShare.isExternal || isExternalPlayer(playerShare.oderId)
            };
          }
        });
      }
    });

    // Loop through each initialized player and calculate totals exactly as ledger does
    Object.keys(balances).forEach(playerId => {
      const p = balances[playerId];
      const ids = p.uniquePlayerIds || [playerId];
      const emails = p.playerEmails || [];

      // 1. Initial donations / Registration fees (deduplicated by label)
      const initialDonations = filteredExpenses.filter(e =>
        (e.isInitialDonation || e.isRegistrationFee) &&
        (e.splitAmong?.some(id => ids.includes(id)) ||
          (e.playerEmail && emails.includes(e.playerEmail.toLowerCase()))) &&
        (e.approvalStatus === 'approved' || !e.approvalStatus) &&
        !e.isDonation
      );

      const seenRegLabels = new Set();
      initialDonations.forEach(fee => {
        const amount = fee.amountPerPlayer !== undefined ? fee.amountPerPlayer : fee.amount;
        const label = fee.isInitialDonation ? getRegistrationFeeLabel({ name: clubName }) : (fee.description || 'Registration Fee');
        
        if (seenRegLabels.has(label)) return;
        seenRegLabels.add(label);

        p.registrationFee += amount;
      });

      // 2. Merchandise
      const merchandise = filteredExpenses.filter(e =>
        e.isMerchandise &&
        ids.includes(e.userId) &&
        (e.approvalStatus === 'approved' || !e.approvalStatus)
      );
      merchandise.forEach(exp => {
        p.merchandiseFees += exp.amount || 0;
      });

      // 3. Game Expenses
      const clubGames = games || [];
      clubGames.forEach(game => {
        if (game.playing11?.some(id => ids.includes(id))) {
          if (game.tournamentId) {
            const tournament = tournaments?.find(t => t.id === game.tournamentId);
            if (tournament && !tournament.isSettled) {
              return; // Skip game expenses until tournament settled
            }
          }

          const gameExpensesList = filteredExpenses.filter(e =>
            e.gameId === game.id &&
            (e.approvalStatus === 'approved' || !e.approvalStatus) &&
            !e.isInitialDonation &&
            !e.isTournamentFee
          );

          const isSuperSub = game.superSubs?.some(id => ids.includes(id));

          gameExpensesList.forEach(expense => {
            const squadSize = game.playing11?.length || 0;
            const superSubCount = game.superSubs?.length || 0;
            const regularPlayerCount = squadSize - superSubCount;

            let playerShare = 0;
            if (squadSize > 0) {
              const totalShares = regularPlayerCount + (superSubCount * 0.5);
              const perPlayerCost = expense.amount / totalShares;
              playerShare = isSuperSub ? perPlayerCost * 0.5 : perPlayerCost;
            }

            const roundedShare = Math.round(playerShare * 100) / 100;
            p.gameExpenses += roundedShare;
          });

          // Late fees
          const hasLateArrival = ids.some(id => game.lateArrivals?.[id]);
          if (game.lateArrivals && hasLateArrival) {
            const lateFee = game.lateFeeAmount ?? 5;
            p.gameExpenses += lateFee;
          }
        }
      });

      // 4. Tournament Fees
      const tournamentWideExpenses = filteredExpenses.filter(e =>
        (e.isTournamentFee || !e.gameId || e.level === 'tournament') &&
        e.tournamentId &&
        (e.approvalStatus === 'approved' || !e.approvalStatus)
      );

      const activeTournaments = tournaments || [];
      activeTournaments.forEach(t => {
        const tExpenses = tournamentWideExpenses.filter(e => e.tournamentId === t.id);

        tExpenses.forEach(fee => {
          if (fee.settled && fee.splitAmongDetails?.length > 0) {
            const playerShare = fee.splitAmongDetails.find(ps =>
              ids.includes(ps.playerId) ||
              ids.includes(ps.oderId) ||
              (ps.email && emails.includes(ps.email.toLowerCase())) ||
              (ps.name && p.name && ps.name.toLowerCase().trim() === p.name.toLowerCase().trim())
            );

            if (playerShare && playerShare.gamesPlayed > 0) {
              const amount = playerShare.amount || 0;
              const perGameShare = amount / playerShare.gamesPlayed;
              const roundedShare = Math.round(perGameShare * 100) / 100;

              const tournamentGames = clubGames.filter(g => g.tournamentId === t.id);
              const playerGames = tournamentGames.filter(g => g.playing11?.some(id => ids.includes(id)));

              p.tournamentFees += roundedShare * playerGames.length;
            }
          } else if (t.isSettled) {
            const tournamentGames = clubGames.filter(g => g.tournamentId === t.id);
            const validGames = tournamentGames.filter(g => g.playing11 && g.playing11.length > 0);
            const numGames = validGames.length;

            if (numGames > 0) {
              const expensePerGame = fee.amount / numGames;

              validGames.forEach(g => {
                if (g.playing11?.some(id => ids.includes(id))) {
                  const squadSize = g.playing11.length;
                  const superSubCount = g.superSubs?.length || 0;
                  const regularCount = squadSize - superSubCount;
                  const totalShares = regularCount + (superSubCount * 0.5);

                  if (totalShares > 0) {
                    const isSuperSub = g.superSubs?.some(id => ids.includes(id));
                    const share = isSuperSub ? (expensePerGame / totalShares) * 0.5 : expensePerGame / totalShares;
                    const roundedShare = Math.round(share * 100) / 100;
                    p.tournamentFees += roundedShare;
                  }
                }
              });
            }
          }
        });
      });

      // Total owes for expenses
      p.owesForExpenses = p.gameExpenses + p.tournamentFees + p.merchandiseFees;

      // 5. Payments & Waivers (from filteredWallet)
      const payments = filteredWallet.filter(t =>
        ids.includes(t.userId) &&
        t.type === 'payment' && t.status === 'approved' && t.amount < 0 && !t.isDonation
      );

      const waivers = filteredWallet.filter(t =>
        ids.includes(t.userId) &&
        t.type === 'waiver' && t.status === 'approved' && t.amount < 0
      );

      p.paymentsMade = payments.reduce((sum, t) => sum + Math.abs(t.amount), 0);
      p.waivers = waivers.reduce((sum, t) => sum + Math.abs(t.amount), 0);

      // Check registration paid / waived status
      p.registrationPaid = payments.some(t => 
        t.isRegistrationFee || 
        t.description?.toLowerCase().includes('benevity') || 
        t.description?.toLowerCase().includes('membership') || 
        t.description?.toLowerCase().includes('registration') ||
        Math.abs(t.amount) === p.registrationFee
      ) || (payments.length > 0 && p.paymentsMade >= p.registrationFee);

      p.registrationWaived = waivers.some(t =>
        t.description?.toLowerCase().includes('membership') ||
        t.description?.toLowerCase().includes('registration') ||
        t.description?.toLowerCase().includes('waived') ||
        Math.abs(t.amount) === p.registrationFee
      ) || (waivers.length > 0 && p.waivers >= p.registrationFee && !p.registrationPaid);

      if (!p.registrationPaid && !p.registrationWaived && p.registrationFee > 0) {
        if (p.paymentsMade >= p.registrationFee) {
          p.registrationPaid = true;
        } else if (p.waivers >= p.registrationFee) {
          p.registrationWaived = true;
        } else if (p.paymentsMade + p.waivers >= p.registrationFee) {
          p.registrationPaid = p.paymentsMade > 0;
          p.registrationWaived = !p.registrationPaid;
        }
      }

      // 6. Out of pocket expenses paid upfront & reimbursements received (from filteredWallet)
      const expenseReimbursements = filteredWallet.filter(t =>
        ids.includes(t.userId) &&
        t.status === 'approved' &&
        (t.type === 'expense_reimbursement' || (t.type === 'reimbursement' && t.amount > 0))
      );
      const reimbursementPaid = filteredWallet.filter(t =>
        ids.includes(t.userId) &&
        t.status === 'approved' &&
        (t.type === 'reimbursement_paid' || (t.type === 'reimbursement' && t.amount < 0))
      );

      const seenExpenseIds = new Set(expenseReimbursements.map(t => t.expenseId).filter(Boolean));

      // Backwards compatibility with direct filteredExpenses addedByPlayerId
      let directExpensesSum = 0;
      filteredExpenses.forEach(exp => {
        if (exp.isRegistrationFee || exp.isTournamentFee || exp.isClubCovered || exp.paidBy === 'club') {
          return;
        }
        if (exp.addedByPlayerId && ids.includes(exp.addedByPlayerId) && exp.approvalStatus === 'approved' && !seenExpenseIds.has(exp.id)) {
          directExpensesSum += exp.amount;
        }
      });

      p.paidUpfront = expenseReimbursements.reduce((sum, t) => sum + t.amount, 0) + directExpensesSum;
      p.reimbursed = reimbursementPaid.reduce((sum, t) => sum + Math.abs(t.amount), 0);

      // Final balance calculation
      const totalOwed = p.registrationFee + p.owesForExpenses;
      const totalPaid = p.paymentsMade + p.waivers;
      const netReimbursementBalance = p.paidUpfront - p.reimbursed;

      p.balance = totalOwed - totalPaid - netReimbursementBalance;

      // Round to 2 decimal places to avoid floating point errors
      p.paidUpfront = Math.round(p.paidUpfront * 100) / 100;
      p.reimbursed = Math.round(p.reimbursed * 100) / 100;
      p.paymentsMade = Math.round(p.paymentsMade * 100) / 100;
      p.waivers = Math.round(p.waivers * 100) / 100;
      p.owesForExpenses = Math.round(p.owesForExpenses * 100) / 100;
      p.tournamentFees = Math.round(p.tournamentFees * 100) / 100;
      p.gameExpenses = Math.round(p.gameExpenses * 100) / 100;
      p.merchandiseFees = Math.round(p.merchandiseFees * 100) / 100;
      p.registrationFee = Math.round(p.registrationFee * 100) / 100;
      p.balance = Math.round(p.balance * 100) / 100;
    });

    // Log warnings if any
    if (warnings.length > 0) {
      console.warn('⚠️ Financial Reports - Data Validation Warnings:', warnings);
    }

    // Convert to array and sort alphabetically by player name
    return Object.entries(balances)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const summary = calculateSummary();
  const playerBalances = calculatePlayerBalances();

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const exportToCSV = () => {
    let csvContent = '';

    if (reportType === 'summary') {
      csvContent = `Financial Summary - ${clubName}\n\n`;
      csvContent += `Total Expenses,${summary.totalExpenses}\n`;
      csvContent += `Total Reimbursements,${summary.totalReimbursements}\n`;
      csvContent += `Net Balance,${summary.netBalance}\n`;
      csvContent += `Pending Expenses,${summary.totalPending}\n\n`;

      csvContent += `\nExpenses by Category\n`;
      csvContent += `Category,Amount\n`;
      Object.entries(summary.byCategory).forEach(([cat, amt]) => {
        csvContent += `${cat},${amt}\n`;
      });
    } else if (reportType === 'game-expenses') {
      csvContent = `Game-by-Game Expenses - ${clubName}\n\n`;

      // IMPORTANT: Only actual expenses, not {getRegistrationFeeLabel({ name: clubName })}
      const actualExpensesForCSV = filteredExpenses.filter(e => !e.isRegistrationFee && !e.isInitialDonation);

      // Get games with expenses
      const gamesWithExpenses = games
        .filter(g => actualExpensesForCSV.some(e => e.gameId === g.id))
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      gamesWithExpenses.forEach(game => {
        const gameExpenses = actualExpensesForCSV.filter(e => e.gameId === game.id);
        const totalGameCost = gameExpenses.reduce((sum, e) => sum + e.amount, 0);
        const squadSize = game.playing11?.length || 0;
        const superSubCount = game.superSubs?.length || 0;
        const regularPlayerCount = squadSize - superSubCount;

        // Calculate per player cost
        let perPlayerCost = 0;
        if (squadSize > 0) {
          const totalShares = regularPlayerCount + (superSubCount * 0.5);
          perPlayerCost = totalShares > 0 ? totalGameCost / totalShares : 0;
        }

        csvContent += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        csvContent += `GAME: ${game.name}\n`;
        csvContent += `Date: ${formatDate(game.date)}\n`;
        if (game.tournamentName) csvContent += `Tournament: ${game.tournamentName}\n`;
        if (game.opponent) csvContent += `Opponent: ${game.opponent}\n`;
        if (game.venue) csvContent += `Venue: ${game.venue}\n`;
        csvContent += `Squad Size: ${squadSize} players`;
        if (superSubCount > 0) csvContent += ` (${superSubCount} super subs)`;
        csvContent += `\n`;
        csvContent += `Total Cost: ${totalGameCost}\n`;
        csvContent += `Per Player: ${perPlayerCost.toFixed(2)}`;
        if (superSubCount > 0) csvContent += ` (Super subs: ${(perPlayerCost * 0.5).toFixed(2)})`;
        csvContent += `\n\n`;

        csvContent += `Type,Description,Submitted By,Amount,Split Among\n`;
        gameExpenses.forEach(exp => {
          const submitterName = exp.createdByName || exp.addedByPlayerName ||
            players.find(p => p.id === exp.addedByPlayerId)?.name ||
            exp.createdBy || 'Unknown';
          csvContent += `${exp.type || 'Other'},"${exp.description || '-'}",${submitterName},${exp.amount},${exp.splitAmong?.length || 0}\n`;
        });

        csvContent += `\nSquad Members:\n`;
        if (squadSize > 0) {
          game.playing11.forEach(playerId => {
            const player = players.find(p => p.id === playerId);
            const isSuperSub = game.superSubs?.includes(playerId);
            const playerShare = isSuperSub ? perPlayerCost * 0.5 : perPlayerCost;
            csvContent += `${player?.name || 'Unknown'},${playerShare.toFixed(2)}${isSuperSub ? ' (Super Sub)' : ''}\n`;
          });
        } else {
          csvContent += `No squad selected\n`;
        }
      });
    } else if (reportType === 'expense-breakdown') {
      csvContent = `Expense Breakdown - ${clubName}\n\n`;

      const summary = calculateSummary();

      // Expenses by Category
      csvContent += `EXPENSES BY CATEGORY\n`;
      csvContent += `Category,Amount\n`;
      Object.entries(summary.byCategory)
        .sort((a, b) => b[1] - a[1]) // Sort by amount descending
        .forEach(([category, amount]) => {
          csvContent += `${category},${amount.toFixed(2)}\n`;
        });

      const totalByCategory = Object.values(summary.byCategory).reduce((sum, amt) => sum + amt, 0);
      csvContent += `TOTAL,${totalByCategory.toFixed(2)}\n\n`;

      // Expenses by Game
      csvContent += `EXPENSES BY GAME\n`;
      csvContent += `Game,Amount\n`;
      Object.entries(summary.byGame)
        .sort((a, b) => b[1] - a[1]) // Sort by amount descending
        .forEach(([gameName, amount]) => {
          csvContent += `"${gameName}",${amount.toFixed(2)}\n`;
        });

      const totalByGame = Object.values(summary.byGame).reduce((sum, amt) => sum + amt, 0);
      csvContent += `TOTAL,${totalByGame.toFixed(2)}\n\n`;

      // All Expenses Detail
      csvContent += `ALL EXPENSES (DETAILED)\n`;
      csvContent += `Date,Type,Description,Game,Amount,Status\n`;
      filteredExpenses
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .forEach(exp => {
          const game = games.find(g => g.id === exp.gameId);
          csvContent += `${formatDate(exp.createdAt)},${exp.type || 'Other'},"${exp.description || '-'}",${game?.name || 'No game'},${exp.amount.toFixed(2)},${exp.approvalStatus}\n`;
        });
    } else if (reportType === 'player-balances') {
      csvContent = `Player Balances - ${clubName}\n\n`;
      csvContent += `Player,Paid Upfront,Reimbursed,Owes (Share),Net Balance,Status\n`;
      playerBalances.forEach(player => {
        const status = player.balance > 0 ? 'Player Owes' : player.balance < 0 ? 'Club Owes' : 'Settled';
        csvContent += `${player.name},${player.paidUpfront},${player.reimbursed},${player.owesForExpenses},${player.balance},${status}\n`;
      });
    } else if (reportType === 'transaction-audit') {
      csvContent = `Transaction Audit Trail - ${clubName}\n\n`;

      // Approved Expenses
      csvContent += `\nAPPROVED EXPENSES\n`;
      csvContent += `Date Submitted,Type,Description,Game,Submitted By,Amount,Split Among,Approved Date\n`;
      expenses
        .filter(e => e.approvalStatus === 'approved')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .forEach(exp => {
          const game = games.find(g => g.id === exp.gameId);
          const submitterName = exp.createdByName || exp.addedByPlayerName ||
            players.find(p => p.id === exp.addedByPlayerId)?.name ||
            exp.createdBy || 'Unknown';
          csvContent += `${formatDate(exp.createdAt)},${exp.type || 'N/A'},"${exp.description || '-'}",${game?.name || 'No game'},${submitterName},${exp.amount},${exp.splitAmong?.length || 0},${exp.approvedAt ? formatDate(exp.approvedAt) : '-'}\n`;
        });

      // Pending Expenses
      csvContent += `\nPENDING EXPENSES\n`;
      csvContent += `Date Submitted,Type,Description,Game,Submitted By,Amount,Days Waiting\n`;
      expenses
        .filter(e => e.approvalStatus === 'pending')
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        .forEach(exp => {
          const game = games.find(g => g.id === exp.gameId);
          const submitterName = exp.createdByName || exp.addedByPlayerName ||
            players.find(p => p.id === exp.addedByPlayerId)?.name ||
            exp.createdBy || 'Unknown';
          const daysWaiting = Math.floor((Date.now() - new Date(exp.createdAt).getTime()) / (1000 * 60 * 60 * 24));
          csvContent += `${formatDate(exp.createdAt)},${exp.type || 'N/A'},"${exp.description || '-'}",${game?.name || 'No game'},${submitterName},${exp.amount},${daysWaiting}\n`;
        });

      // Denied Expenses
      csvContent += `\nDENIED EXPENSES\n`;
      csvContent += `Date Submitted,Type,Description,Game,Submitted By,Amount,Denial Reason,Denied Date\n`;
      expenses
        .filter(e => e.approvalStatus === 'denied')
        .sort((a, b) => new Date(b.deniedAt || b.createdAt) - new Date(a.deniedAt || a.createdAt))
        .forEach(exp => {
          const game = games.find(g => g.id === exp.gameId);
          const submitterName = exp.createdByName || exp.addedByPlayerName ||
            players.find(p => p.id === exp.addedByPlayerId)?.name ||
            exp.createdBy || 'Unknown';
          csvContent += `${formatDate(exp.createdAt)},${exp.type || 'N/A'},"${exp.description || '-'}",${game?.name || 'No game'},${submitterName},${exp.amount},"${exp.denialReason || 'No reason provided'}",${exp.deniedAt ? formatDate(exp.deniedAt) : '-'}\n`;
        });

      // Reimbursements
      csvContent += `\nREIMBURSEMENTS\n`;
      csvContent += `Date,Player,Amount,Notes\n`;
      filteredWallet
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .forEach(txn => {
          const player = players.find(p => p.id === txn.userId);
          csvContent += `${formatDate(txn.createdAt)},${player?.name || 'Unknown'},${txn.amount},"${txn.notes || '-'}"\n`;
        });
    } else if (reportType === 'players-report') {
      csvContent = `Players Report - ${clubName}\n\n`;
      csvContent += `First Name,Last Name,Email,Phone\n`;
      
      const sortedPlayers = [...(players || [])].sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : '';
        const nameB = b.name ? b.name.toLowerCase() : '';
        return nameA.localeCompare(nameB);
      });
      
      sortedPlayers.forEach(player => {
        const fullName = player.name || '';
        const nameParts = fullName.trim().split(' ');
        let firstName = nameParts[0] || '';
        let lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
        
        csvContent += `"${firstName}","${lastName}","${player.email || '-'}","${player.phone || player.phoneNumber || '-'}"\n`;
      });
    } else if (reportType === 'tournament-player-summary') {
      const tournament = tournaments?.find(t => t.id === selectedTournament);
      csvContent = `Tournament Player Summary - ${clubName}\n\n`;

      // IMPORTANT: Only actual expenses, not Initial Donations
      const actualExpensesForCSV = filteredExpenses.filter(e => !e.isRegistrationFee);

      if (tournament) {
        csvContent += `Tournament: ${tournament.name}\n`;
        csvContent += `Start Date: ${formatDate(tournament.startDate)}\n`;
        if (tournament.endDate) {
          csvContent += `End Date: ${formatDate(tournament.endDate)}\n`;
          csvContent += `Status: Completed\n`;
        } else {
          csvContent += `Status: Ongoing\n`;
        }
        csvContent += `\n`;
      }

      // Calculate player expenses (same logic as in report)
      const tournamentGames = selectedTournament === 'all'
        ? games
        : games.filter(g => g.tournamentId === selectedTournament);

      const playerExpensesData = [];

      players.forEach(player => {
        const playerGames = tournamentGames.filter(game =>
          game.playing11 && game.playing11.includes(player.id)
        );

        if (playerGames.length > 0) {
          let totalOwed = 0;
          const gameDetails = [];

          playerGames.forEach(game => {
            const gameExpenses = actualExpensesForCSV.filter(exp => exp.gameId === game.id);
            const totalGameCost = gameExpenses.reduce((sum, exp) => sum + exp.amount, 0);

            if (totalGameCost > 0) {
              const squadSize = game.playing11?.length || 0;
              const superSubCount = game.superSubs?.length || 0;
              const regularPlayerCount = squadSize - superSubCount;
              const isSuperSub = game.superSubs?.includes(player.id);
              const isLate = game.lateArrivals?.[player.id];
              const LATE_FEE = game.lateFeeAmount ?? 5;

              let playerShare = 0;
              if (squadSize > 0) {
                const totalShares = regularPlayerCount + (superSubCount * 0.5);
                const perPlayerCost = totalShares > 0 ? totalGameCost / totalShares : 0;
                playerShare = isSuperSub ? perPlayerCost * 0.5 : perPlayerCost;
              }

              // Add late fee if player was late
              if (isLate) {
                playerShare += LATE_FEE;
              }

              totalOwed += playerShare;
              gameDetails.push({
                gameName: game.name,
                gameDate: game.date,
                playerShare: playerShare.toFixed(2),
                isSuperSub,
                isLate,
                lateFeeAmount: game.lateFeeAmount
              });
            }
          });

          const playerPaidExpenses = expenses.filter(exp =>
            exp.addedByPlayerId === player.id &&
            exp.approvalStatus === 'approved' &&
            (selectedTournament === 'all' || tournamentGames.some(g => g.id === exp.gameId))
          );
          const paidUpfront = playerPaidExpenses.reduce((sum, exp) => sum + exp.amount, 0);

          const playerReimbursements = walletTransactions.filter(txn => txn.userId === player.id);
          const reimbursed = playerReimbursements.reduce((sum, txn) => sum + txn.amount, 0);

          const balance = totalOwed - (paidUpfront - reimbursed);

          playerExpensesData.push({
            name: player.name,
            gamesPlayed: playerGames.length,
            totalGames: tournamentGames.length,
            totalOwed: totalOwed.toFixed(2),
            paidUpfront: paidUpfront.toFixed(2),
            reimbursed: reimbursed.toFixed(2),
            balance: balance.toFixed(2),
            gameDetails
          });
        }
      });

      // Summary table
      csvContent += `PLAYER SUMMARY\n`;
      csvContent += `Player,Games Played,Total Owed,Paid Upfront,Reimbursed,Balance,Status\n`;
      playerExpensesData.forEach(p => {
        const status = Math.abs(parseFloat(p.balance)) < 0.01 ? 'Settled' : parseFloat(p.balance) > 0 ? 'Owes' : 'Credit';
        csvContent += `${p.name},${p.gamesPlayed}/${p.totalGames},${p.totalOwed},${p.paidUpfront},${p.reimbursed},${p.balance},${status}\n`;
      });

      // Detailed breakdown per player
      csvContent += `\n\nDETAILED GAME-BY-GAME BREAKDOWN\n\n`;
      playerExpensesData.forEach(playerData => {
        csvContent += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        csvContent += `PLAYER: ${playerData.name}\n`;
        csvContent += `Games Played: ${playerData.gamesPlayed}/${playerData.totalGames}\n`;
        csvContent += `Total Owed: ${playerData.totalOwed}\n`;
        csvContent += `Balance: ${playerData.balance}\n\n`;

        csvContent += `Game,Date,Player Share,Role\n`;
        playerData.gameDetails.forEach(game => {
          const role = game.isSuperSub ? 'Super Sub' : game.isLate ? `Regular (Late +$${game.lateFeeAmount ?? 5})` : 'Regular';
          csvContent += `"${game.gameName}",${formatDate(game.gameDate)},${game.playerShare},${role}\n`;
        });
        csvContent += `\n`;
      });
    } else if (reportType === 'registrations') {
      csvContent = `${getRegistrationFeeLabel({ name: clubName })} Report - ${clubName}\n\n`;

      // Get all UNIQUE players from teams (deduplicate by id/userId)
      const playerMap = new Map();
      (teams || []).forEach(team => {
        if (team.players) {
          team.players.forEach(player => {
            const playerId = player.userId || player.id;
            if (!playerMap.has(playerId)) {
              playerMap.set(playerId, {
                ...player,
                odometerId: playerId,
                teamId: team.id,
                teamName: team.name,
                allTeams: [team.name]
              });
            } else {
              const existingPlayer = playerMap.get(playerId);
              existingPlayer.allTeams.push(team.name);
            }
          });
        }
      });

      const allPlayers = Array.from(playerMap.values());

      // Calculate stats
      let paidCount = 0;
      let unpaidCount = 0;
      let totalCollected = 0;
      let totalOutstanding = 0;

      allPlayers.forEach(player => {
        const playerId = player.userId || player.id;
        const regFee = expenses.find(e =>
          e.isRegistrationFee &&
          (e.splitAmong?.includes(player.id) || e.splitAmong?.includes(playerId) ||
            e.playerEmail?.toLowerCase() === player.email?.toLowerCase())
        );

        if (regFee) {
          // Look for payment transactions (type='payment', negative amount)
          const playerPayment = walletTransactions.find(t =>
            t.type === 'payment' &&
            t.status === 'approved' &&
            t.amount < 0 &&
            (t.userId === player.id || t.userId === playerId ||
              t.playerEmail?.toLowerCase() === player.email?.toLowerCase())
          );

          if (playerPayment) {
            paidCount++;
            totalCollected += Math.abs(playerPayment.amount);
          } else {
            unpaidCount++;
            totalOutstanding += regFee.amount || 250;
          }
        } else {
          unpaidCount++;
          totalOutstanding += 250;
        }
      });

      const collectionRate = allPlayers.length > 0 ? (paidCount / allPlayers.length) * 100 : 0;

      // Summary section
      csvContent += `SUMMARY\n`;
      csvContent += `Total Players,${allPlayers.length}\n`;
      csvContent += `Paid,${paidCount}\n`;
      csvContent += `Unpaid,${unpaidCount}\n`;
      csvContent += `Total Collected,$${totalCollected.toFixed(2)}\n`;
      csvContent += `Total Outstanding,$${totalOutstanding.toFixed(2)}\n`;
      csvContent += `Collection Rate,${collectionRate.toFixed(1)}%\n`;
      csvContent += `\n`;

      // Player details
      csvContent += `PLAYER DETAILS\n`;
      csvContent += `Player,Email,Team(s),Joined Date,${getRegistrationFeeLabel({ name: clubName })} Created,Payment Status,Amount\n`;

      allPlayers
        .sort((a, b) => {
          const aId = a.userId || a.id;
          const bId = b.userId || b.id;
          const aRegFee = expenses.find(e => e.isRegistrationFee && (e.splitAmong?.includes(a.id) || e.splitAmong?.includes(aId)));
          const bRegFee = expenses.find(e => e.isRegistrationFee && (e.splitAmong?.includes(b.id) || e.splitAmong?.includes(bId)));
          const aWallet = walletTransactions.find(t => (t.userId === a.id || t.userId === aId) && t.expenseId === aRegFee?.id && t.status === 'approved');
          const bWallet = walletTransactions.find(t => (t.userId === b.id || t.userId === bId) && t.expenseId === bRegFee?.id && t.status === 'approved');
          const aPaid = aWallet && aWallet.amount >= 250;
          const bPaid = bWallet && bWallet.amount >= 250;

          if (aPaid === bPaid) return a.name.localeCompare(b.name);
          return aPaid ? 1 : -1;
        })
        .forEach(player => {
          const playerId = player.userId || player.id;
          const regFee = expenses.find(e =>
            e.isRegistrationFee &&
            (e.splitAmong?.includes(player.id) || e.splitAmong?.includes(playerId))
          );

          const playerPayment = walletTransactions.find(t =>
            t.type === 'payment' &&
            t.status === 'approved' &&
            t.amount < 0 &&
            (t.userId === player.id || t.userId === playerId ||
              t.playerEmail?.toLowerCase() === player.email?.toLowerCase())
          );

          const isPaid = !!playerPayment;
          const hasRegFee = !!regFee;
          const joinedDate = player.joinedAt ? formatDate(player.joinedAt) : 'Unknown';
          const teamsStr = (player.allTeams || [player.teamName]).join('; ');

          csvContent += `${player.name},${player.email},"${teamsStr}",${joinedDate},${hasRegFee ? 'Yes' : 'No'},${isPaid ? 'Paid' : 'Unpaid'},$${hasRegFee ? '250.00' : '0.00'}\n`;
        });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clubName}-${reportType}-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Financial Reports</h2>
        <p className="text-gray-600">Complete financial transparency for {clubName}</p>
      </div>

      {/* Role Indicator Banner for Non-Treasurers */}
      {!canApproveExpenses && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <div>
              <h3 className="font-semibold text-blue-900">View-Only Mode</h3>
              <p className="text-sm text-blue-700 mt-1">
                You can view all financial reports, but only treasurers can approve or deny expenses and payments.
                {treasurerInfo && (
                  <span className="block mt-1">
                    Contact <strong>{treasurerInfo.name}</strong> ({treasurerInfo.email}) for approvals.
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Treasurer Action Required Alert */}
      {canApproveExpenses && expenses.filter(e => e.approvalStatus === 'pending').length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-yellow-400 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Treasurer Action Required
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                You have {expenses.filter(e => e.approvalStatus === 'pending').length} expense(s) awaiting your approval.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Audit Documentation Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection('audit-docs')}
        >
          <div className="flex items-center gap-3">
            <FileText className="text-blue-600" size={24} />
            <div>
              <h3 className="text-lg font-bold text-blue-900">Audit Documentation</h3>
              <p className="text-sm text-blue-700">How all calculations are performed - 100% transparent</p>
            </div>
          </div>
          {expandedSections['audit-docs'] ? <ChevronUp className="text-blue-600" /> : <ChevronDown className="text-blue-600" />}
        </div>

        {expandedSections['audit-docs'] && (
          <div className="mt-6 bg-white rounded-lg p-6 space-y-6">
            {/* Quick Reference */}
            <div>
              <h4 className="text-md font-bold text-gray-900 mb-3">📊 Calculation Formulas</h4>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm space-y-2">
                <div className="border-b border-gray-200 pb-2 mb-2">
                  <p className="font-semibold text-gray-700">Club Level:</p>
                </div>
                <p className="text-gray-700">totalExpenses = Σ(approved expense.amount)</p>
                <p className="text-gray-700">totalReimbursements = Σ(walletTransaction.amount)</p>
                <p className="text-gray-700">netBalance = totalExpenses - totalReimbursements</p>

                <div className="border-b border-gray-200 pb-2 mb-2 mt-4">
                  <p className="font-semibold text-gray-700">Player Level:</p>
                </div>
                <p className="text-gray-700">paidUpfront = Σ(expenses where player paid)</p>
                <p className="text-gray-700">reimbursed = Σ(wallet transactions for player)</p>
                <p className="text-gray-700">owesForExpenses = Σ(player's share of divided expenses)</p>
                <p className="text-blue-600 font-semibold mt-2">balance = owesForExpenses - (paidUpfront - reimbursed)</p>

                <div className="border-b border-gray-200 pb-2 mb-2 mt-4">
                  <p className="font-semibold text-gray-700">Super Sub Discount:</p>
                </div>
                <p className="text-gray-700">regularPlayer: pays 100% of share</p>
                <p className="text-gray-700">superSub: pays 50% of share</p>
              </div>
            </div>

            {/* Balance Interpretation */}
            <div>
              <h4 className="text-md font-bold text-gray-900 mb-3">🎯 Understanding Balances</h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="bg-red-100 rounded-full p-2 mt-1">
                    <TrendingUp className="text-red-600" size={16} />
                  </div>
                  <div>
                    <p className="font-semibold text-red-900">Positive Balance (Red)</p>
                    <p className="text-sm text-red-700">Player owes the club money. They need to pay this amount.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="bg-green-100 rounded-full p-2 mt-1">
                    <TrendingDown className="text-green-600" size={16} />
                  </div>
                  <div>
                    <p className="font-semibold text-green-900">Negative Balance (Green)</p>
                    <p className="text-sm text-green-700">Club owes the player money. They should be reimbursed.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="bg-gray-100 rounded-full p-2 mt-1">
                    <Users className="text-gray-600" size={16} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Zero Balance (Gray)</p>
                    <p className="text-sm text-gray-700">All settled. Player has paid their share and been reimbursed.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Worked Example */}
            <div>
              <h4 className="text-md font-bold text-gray-900 mb-3">📝 Example Calculation</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="font-semibold text-blue-900 mb-2">John's Transactions:</p>
                <ul className="space-y-1 text-sm text-blue-800 mb-3">
                  <li>• Paid upfront: $200 (venue rental)</li>
                  <li>• Reimbursed: $150</li>
                  <li>• Owes (his share): $80</li>
                </ul>

                <div className="bg-white rounded p-3 font-mono text-sm space-y-1">
                  <p className="text-gray-700">netPaidByPlayer = $200 - $150 = $50</p>
                  <p className="text-gray-700">balance = $80 - $50 = <span className="text-red-600 font-bold">+$30</span></p>
                  <p className="text-red-600 font-semibold mt-2">Status: John owes club $30</p>
                </div>

                <p className="text-xs text-blue-700 mt-3">
                  ✓ John is still out of pocket $50, but he owes $80 total, so he needs to pay an additional $30.
                </p>
              </div>
            </div>

            {/* Data Sources */}
            <div>
              <h4 className="text-md font-bold text-gray-900 mb-3">🗄️ Data Sources</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="font-semibold text-purple-900 text-sm mb-1">Expenses Collection</p>
                  <p className="text-xs text-purple-700">All approved club expenses with amount, splitAmong, addedByPlayerId</p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="font-semibold text-green-900 text-sm mb-1">Wallet Transactions</p>
                  <p className="text-xs text-green-700">All reimbursements paid to players with amount and userId</p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="font-semibold text-orange-900 text-sm mb-1">Games Collection</p>
                  <p className="text-xs text-orange-700">Game data including superSubs array for 50% discount</p>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="font-semibold text-indigo-900 text-sm mb-1">Players Collection</p>
                  <p className="text-xs text-indigo-700">All club players with id and name for tracking balances</p>
                </div>
              </div>
            </div>

            {/* Audit Verification */}
            <div>
              <h4 className="text-md font-bold text-gray-900 mb-3">✅ Automatic Checks</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">The system automatically verifies:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>✓ Sum of all player "Paid Upfront" = Total Expenses</li>
                  <li>✓ Sum of all player "Reimbursed" = Total Reimbursements</li>
                  <li>✓ All calculations rounded to 2 decimal places (no floating point errors)</li>
                  <li>✓ All player IDs validated against player list</li>
                  <li>✓ All expense amounts are valid numbers</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3 italic">
                  Check browser console for detailed reconciliation logs
                </p>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
              <h4 className="text-md font-bold text-yellow-900 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Important Notes
              </h4>
              <ul className="space-y-1 text-sm text-yellow-800">
                <li>• Super subs are automatically detected from game data and charged 50%</li>
                <li>• Only approved expenses are included in calculations</li>
                <li>• Pending expenses are tracked separately and not included in balances</li>
                <li>• All amounts are in club's currency (no conversion)</li>
                <li>• Export CSV for detailed audit trail and spot-checking</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="summary">Financial Summary</option>
              <option value="player-balances">Player Balances</option>
              <option value="expense-breakdown">Expense Breakdown</option>
              <option value="game-expenses">Game-by-Game Expenses</option>
              <option value="tournament-player-summary">Tournament Player Summary</option>
              <option value="registrations">Registrations</option>
              <option value="transaction-audit">Transaction Audit Trail</option>
              <option value="players-report">Players Report</option>
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="all-time">All Time</option>
              <option value="this-month">This Month</option>
              <option value="last-month">Last Month</option>
              <option value="this-year">This Year</option>
              <option value="last-year">Last Year</option>
            </select>
          </div>

          {/* Only show Tournament filter for relevant report types */}
          {(reportType === 'summary' ||
            reportType === 'player-balances' ||
            reportType === 'tournament-player-summary' ||
            reportType === 'game-expenses') && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tournament
                </label>
                <select
                  value={selectedTournament}
                  onChange={(e) => setSelectedTournament(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">All Tournaments</option>
                  {tournaments && tournaments.map(tournament => (
                    <option key={tournament.id} value={tournament.id}>
                      {tournament.name}
                      {tournament.endDate && ' ✓'}
                    </option>
                  ))}
                </select>
              </div>
            )}

          <div className="flex items-end">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Tournament Filter Notice */}
      {selectedTournament && selectedTournament !== 'all' && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="bg-purple-600 text-white rounded-full p-2">
              <Filter size={20} />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-purple-900 mb-1">
                Tournament Filter Active
              </h4>
              <p className="text-sm text-purple-800">
                {(() => {
                  const tournament = tournaments?.find(t => t.id === selectedTournament);
                  return tournament ? (
                    <>
                      Showing data for <span className="font-semibold">{tournament.name}</span> only.
                      All expenses, games, and reimbursements filtered to this tournament.
                    </>
                  ) : 'Showing tournament-specific data.';
                })()}
              </p>
            </div>
            <button
              onClick={() => setSelectedTournament('all')}
              className="text-purple-600 hover:text-purple-800 font-semibold text-sm whitespace-nowrap"
            >
              Clear Filter
            </button>
          </div>
        </div>
      )}

      {/* Report Content */}
      {reportType === 'summary' && (
        <SummaryReport
          summary={summary}
          filteredExpenses={filteredExpenses}
          formatCurrency={formatCurrency}
          clubName={clubName}
        />
      )}

      {reportType === 'player-balances' && (
        <PlayerBalancesReport
          playerBalances={playerBalances}
          formatCurrency={formatCurrency}
          clubName={clubName}
          expenses={expenses}
          walletTransactions={walletTransactions}
          games={games}
          tournaments={tournaments}
          teams={teams}
          paymentSubmissions={paymentSubmissions}
          players={players}
        />
      )}

      {reportType === 'expense-breakdown' && (
        <ExpenseBreakdownReport
          filteredExpenses={filteredExpenses}
          games={games}
          formatCurrency={formatCurrency}
        />
      )}

      {reportType === 'game-expenses' && (
        <GameExpensesReport
          games={games}
          filteredExpenses={filteredExpenses}
          players={players}
          formatCurrency={formatCurrency}
        />
      )}

      {reportType === 'transaction-audit' && (
        <TransactionAuditReport
          expenses={expenses}
          filteredWallet={filteredWallet}
          players={players}
          games={games}
          walletTransactions={walletTransactions}
          formatCurrency={formatCurrency}
          clubName={clubName}
        />
      )}

      {/* Tournament Player Summary Report */}
      {reportType === 'tournament-player-summary' && (
        <TournamentPlayerSummary
          selectedTournament={selectedTournament}
          tournaments={tournaments}
          games={games}
          players={players}
          expenses={expenses}
          filteredExpenses={filteredExpenses}
          walletTransactions={walletTransactions}
          formatCurrency={formatCurrency}
        />
      )}

      {/* REGISTRATIONS REPORT */}
      {reportType === 'registrations' && (
        <RegistrationsReport
          teams={teams}
          players={players}
          expenses={expenses}
          walletTransactions={walletTransactions}
          formatCurrency={formatCurrency}
        />
      )}

      {/* PLAYERS REPORT */}
      {reportType === 'players-report' && (
        <PlayersReport players={players} />
      )}
    </div>
  );
};

export default FinancialReports;